import type { BrowserHistory } from 'history'
import type { QueryStringClient } from './queryStringClient'
import type { BrowserQueryString } from '@/scopes/common/infrastructure/query-strings/types/browserQueryString'
import { getPassThroughParams } from '@/scopes/search/search/domain/getPassThroughParams'
import { createQueryStringClient } from './queryStringClient'

// TODO We would like replace this implementation with the one in combinedBrowserQueryString but removing missing filters
export function createBrowserQueryStringKeepingParams<T>({
  passThroughParams,
  queryStringClient,
  initialHistory,
}: {
  passThroughParams?: (keyof T)[]
  queryStringClient?: QueryStringClient
  initialHistory?: BrowserHistory
}): BrowserQueryString {
  const qsClient = queryStringClient || createQueryStringClient({ initialHistory })

  function set(queryString: string) {
    const existingQueryString = qsClient.get()
    const passThroughQueryParams = getPassThroughParams(
      new URLSearchParams(existingQueryString),
      passThroughParams ?? [],
    )
    const searchString = combineParams<T>(existingQueryString, queryString, passThroughQueryParams)
    qsClient.set(searchString)
  }

  return {
    get: qsClient.get,
    set,
  }
}

function combineParams<T>(currentParams: string, incomingParams: string, passThroughQueryParams: Partial<T>) {
  const incomingParamObject = parseParamsToObject(incomingParams)
  const currentParamsObject = parseParamsToObject(currentParams)
  const utmParams = getParamsToKeepFromCurrentParams(currentParamsObject)

  const result = { ...incomingParamObject, ...utmParams, ...passThroughQueryParams }
  const resultEntries = Object.entries(result)

  return new URLSearchParams(resultEntries).toString()
}

function parseParamsToObject(params: string) {
  const urlParams = new URLSearchParams(params)
  return Object.fromEntries(urlParams.entries())
}

function getParamsToKeepFromCurrentParams(currentParamsObject: Record<string, string>) {
  return Object.fromEntries(Object.entries(currentParamsObject).filter(([key]) => isParamToKeep(key)))
}

const paramsToKeep = new Set([
  'gclid',
  'gclsrc',
  'dclid',
  'wbraid',
  'gbraid',
  'fbclid',
  'fbc',
  'fbp',
  'fbtrace_id',
  'scclid',
  'pin_id',
  'ttclid',
  'li_fat_id',
  'igclid',
  'smc_upi',
  'rtbhc',
  'msclkid',
  'ranEAID',
  'ranCID',
  'ranMID',
  'ranSubID',
  'TDUID',
  'Fid',
  'cid',
  'pid',
  'tid',
  'tam',
  'agentCode',
  'nui',
  'm_i',
  'kb',
  'epik',
])

function isParamToKeep(key: string) {
  return key.startsWith('utm_') || key.startsWith('ptm_') || paramsToKeep.has(key)
}
